import { useState, useEffect } from 'react';
import { StorageService } from '@nextgen-web-framework/core';
import {
  AmwayUserDetails,
  AmwayUserDetailsStatusEnum,
  CartConstantsEnum,
  KeyboardKeys,
  cartApi,
  getActiveCart,
  getInstallProductDetails,
  isRegularOrder,
} from '@commerce-cart-webcomponents/sdk';
import { useTranslation } from '@nextgen-web-framework/all';
import { checkKeyPressOrClick } from '../../../utils/commonUtils';
import { DrawerForm } from './drawer-form/DrawerForm';
import { AccountSearchDrawerProps } from './types/propsType';
import { KEY_DELAY } from './types/enums';

function getAccountData({
  searchString,
  limit = '0',
  offset = '10',
}: {
  searchString: string;
  limit?: string;
  offset?: string;
}) {
  return cartApi
    .getProfiles({
      cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
      searchString,
      limit,
      offset,
    })
    .then(
      (res) =>
        res.accountSearch?.map((account) => ({
          amwayUserId: account?.profile?.profileId,
          accountId: String(account.accountNumber),
          primaryName: account?.accountName?.name ?? '',
          status: account.status ?? AmwayUserDetailsStatusEnum.Active,
        })) as AmwayUserDetails[],
    );
}

function handleAccountSearch(
  e: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLSpanElement, MouseEvent>,
  data: {
    text: string;
    aboAccountId: string;
    offset?: string;
    limit?: string;
  },
  callback: (profileResponse: AmwayUserDetails[]) => void,
  catchCallback?: () => void,
) {
  e.preventDefault();
  getAccountData({
    searchString: data.text,
    offset: data.offset,
    limit: data.limit,
  })
    .then((res: AmwayUserDetails[]) => {
      callback(res);
    })
    .catch(catchCallback);
}

export const AccountSearchDrawer = ({
  cartData,
  onPrimaryButtonClick,
  onSecondaryClick,
  onRadioChange,
  primaryButtonText,
  secondaryButtonText,
  drawerHeaderTitle,
  resultsTitle,
  placeHolderText,
  subHeading,
  hidePrimaryButton = false,
  showActiveCart = false,
  showDisableCards = true,
  showSwitchModal,
  modalConfirmClick,
  onClose,
}: AccountSearchDrawerProps) => {
  const t = useTranslation();
  const [profiles, setProfiles] = useState<AmwayUserDetails[]>([]);
  const [text, setText] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  const [checked, setChecked] = useState<number>(-1);
  const [limit, setLimit] = useState(100);
  const [showNoResultText, setShowNoResultText] = useState(false);
  const singleResultTitle = t('cart.accountSearchDrawer.singleResultTitle');
  const multipleResultTitle = t('cart.accountSearchDrawer.multipleResultTitle');
  const defaultResultTitle = profiles?.length > 1 ? multipleResultTitle : singleResultTitle;

  function searchIconHandler(e: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLSpanElement, MouseEvent>) {
    if (!checkKeyPressOrClick(e, KeyboardKeys.ENTER)) {
      return;
    }

    if ((e.target as HTMLInputElement).value.length > 3) {
      setShowLoader(true);
      handleAccountSearch(
        e,
        {
          aboAccountId: cartData.amwayUser.accountId as string,
          text,
          limit: String(limit),
          offset: '0',
        },
        (res: AmwayUserDetails[]) => {
          setShowLoader(false);
          setProfiles(res);
        },
        () => setShowLoader(false),
      );
    }
  }

  function handleDrawerClose() {
    setProfiles([]);
    setChecked(-1);
    onPrimaryButtonClick?.();
  }

  useEffect(() => {
    setChecked(-1);
    setShowNoResultText(false);
    const inputDebounce = setTimeout(async () => {
      const trimedText = text.trimStart().trimEnd();
      if (trimedText.length < 3) {
        setProfiles([]);
        return;
      }

      setShowLoader(true);
      try {
        const res = await getAccountData({
          searchString: trimedText,
          limit: String(limit),
          offset: '0',
        });
        setShowNoResultText(!res?.length);
        setProfiles(res);
      } catch {
        setShowNoResultText(true);
      } finally {
        setShowLoader(false);
      }
    }, KEY_DELAY);

    return () => clearTimeout(inputDebounce);
  }, [text]);

  const isSopEntryExistInCart = () => {
    const activeCart = getActiveCart(cartData);
    return !!activeCart?.entries?.find(({ sopElementId }) => Boolean(sopElementId));
  };

  return (
    <DrawerForm
      onKeyDown={searchIconHandler}
      onSearchClick={searchIconHandler}
      onDrawerClose={handleDrawerClose}
      onCancelClick={handleDrawerClose}
      onConfirmClick={onSecondaryClick}
      showDisableCards={showDisableCards}
      data={profiles}
      headerTitle={drawerHeaderTitle}
      inputPlaceHolderText={placeHolderText}
      inputValue={text}
      showActiveCarts={showActiveCart}
      onTextChange={(e) => setText(e.target.value)}
      checkedIndex={checked}
      disableConfirmButton={checked === -1}
      showLoader={showLoader}
      hidePrimaryButton={hidePrimaryButton}
      showNoResultText={showNoResultText}
      onRadioChange={({ amwayUserId, accountId, primaryName }, i) => {
        const newSelectedProfile = {
          amwayUserId,
          accountId,
          primaryName,
        };
        setChecked(i);
        onRadioChange?.(newSelectedProfile);
      }}
      interSectionObserverCallBack={(text) => {
        if (text.length > 0) {
          setLimit((limit) => {
            setShowLoader(true);
            getAccountData({
              searchString: text,
              limit: String(limit + 5),
              offset: '0',
            })
              .then((res: AmwayUserDetails[]) => {
                setShowLoader(false);
                setProfiles(res);
              })
              .catch(() => setShowLoader(false));

            return limit + 5;
          });
        }
      }}
      subHeading={subHeading}
      resultTitle={resultsTitle ?? defaultResultTitle}
      cancelButtonTitle={primaryButtonText}
      confirmButtonTitle={secondaryButtonText}
      showSwitchModal={showSwitchModal}
      modalConfirmClick={modalConfirmClick}
      isSopEntryExist={isSopEntryExistInCart()}
      isInstallmentExist={
        isRegularOrder(cartData.orderType) && getInstallProductDetails(getActiveCart(cartData)?.entries).length > 0
      }
      onClose={onClose}
    />
  );
};
